/**
 * VendorGallery — public storefront gallery grid.
 * URL: /vendor/:id/gallery
 * Renders the vendor's cover image (hero), all menu-item photos, and any
 * additional gallery photos the vendor uploaded. Uses a masonry-ish CSS
 * columns layout so images of different aspect ratios flow naturally.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ImageIcon, Loader2, X, ShoppingBag } from 'lucide-react';
import api from '../services/api';
import Header from '../components/Header';
import { useApp } from '../context/AppContext';

const VendorGallery = () => {
  const { id } = useParams();
  const nav = useNavigate();
  const { format } = useApp();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/vendors/${id}/gallery`);
        if (alive) setData(r.data);
      } catch {
        if (alive) setData(null);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [id]);

  const items = useMemo(() => {
    if (!data) return [];
    const out = [];
    if (data.cover) out.push({ url: data.cover, kind: 'cover' });
    out.push(...(data.gallery_images || []));
    out.push(...(data.menu_images || []));
    return out;
  }, [data]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="max-w-6xl mx-auto py-24 flex justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="max-w-md mx-auto py-24 text-center">
          <ImageIcon className="h-10 w-10 mx-auto text-slate-300" />
          <p className="mt-3 text-slate-500">Gallery not found.</p>
        </div>
      </div>
    );
  }

  const v = data.vendor;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="vendor-gallery">
      <Header />
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-6">
        <button
          onClick={() => nav(`/vendor/${id}`)}
          className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
          data-testid="gallery-back"
        >
          <ArrowLeft className="h-4 w-4" /> Back to store
        </button>
        <div className="mt-3 flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900">
              {v.name} · Gallery
            </h1>
            <p className="text-slate-500 mt-1 text-sm">
              {items.length} photo{items.length === 1 ? '' : 's'} · {v.cuisine || v.category}
            </p>
          </div>
          <button
            onClick={() => nav(`/vendor/${id}`)}
            className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm px-4 py-2 rounded-full"
            data-testid="gallery-order-btn"
          >
            <ShoppingBag className="h-4 w-4" /> Order from menu
          </button>
        </div>
      </section>

      {items.length === 0 ? (
        <div className="max-w-md mx-auto py-24 text-center">
          <ImageIcon className="h-10 w-10 mx-auto text-slate-300" />
          <p className="mt-3 text-slate-500">This store hasn&apos;t uploaded any photos yet.</p>
        </div>
      ) : (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 mt-6 pb-16">
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-3 [&>*]:mb-3" data-testid="gallery-grid">
            {items.map((it, idx) => (
              <button
                key={`${it.kind}-${idx}`}
                type="button"
                onClick={() => setLightbox(it)}
                className="group relative w-full break-inside-avoid overflow-hidden rounded-xl bg-slate-200 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                data-testid={`gallery-tile-${idx}`}
              >
                <img
                  src={it.url}
                  alt={it.item_name || it.caption || v.name}
                  loading="lazy"
                  className="w-full block transition-transform duration-300 group-hover:scale-[1.03]"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
                {(it.item_name || it.caption) && (
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-3 text-left text-white">
                    <div className="text-sm font-medium truncate">{it.item_name || it.caption}</div>
                    {it.price != null && (
                      <div className="text-xs opacity-90">{format(it.price, v.currency)}</div>
                    )}
                  </div>
                )}
                {it.kind === 'cover' && (
                  <span className="absolute top-2 left-2 text-[10px] uppercase tracking-wide bg-white/85 text-slate-900 px-2 py-0.5 rounded-full font-semibold">
                    Cover
                  </span>
                )}
              </button>
            ))}
          </div>
        </section>
      )}

      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
          data-testid="gallery-lightbox"
        >
          <button
            className="absolute top-4 right-4 text-white/80 hover:text-white"
            onClick={() => setLightbox(null)}
            aria-label="Close"
          >
            <X className="h-6 w-6" />
          </button>
          <img
            src={lightbox.url}
            alt={lightbox.item_name || lightbox.caption || v.name}
            className="max-h-[85vh] max-w-full rounded-xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default VendorGallery;
